/**
 * CIE-Lab colour maths, ported line for line from `com.vosviewer.util.Colors`.
 *
 * Why Lab and not RGB: VOSviewer interpolates every ramp — the density scale,
 * the overlay scale, the light/dark variants of a cluster colour — in Lab.  A
 * naive RGB lerp between, say, viridis' blue and green puts a muddy grey-green
 * band in the middle where Lab puts a clean transition, and the difference is
 * plainly visible on a density map.  Reproducing Lab is therefore not
 * pedantry; it is the difference between our output and VOSviewer's.
 *
 * The constants below are VOSviewer's, including its slightly unusual choices:
 * the D65 white point is (95.047, 100, 108.883), the sRGB companding uses
 * `< 0.04045` (not `<=`), and `LABToXYZ` compares `L > 7.9996248` rather than
 * recomputing the epsilon.  Keeping them means our rounding matches its
 * rounding, which matters when a colour lands exactly on a 0.5 boundary.
 */

import type { RGB } from '../types';

const XYZ_WHITE_REFERENCE_X = 95.047;
const XYZ_WHITE_REFERENCE_Y = 100.0;
const XYZ_WHITE_REFERENCE_Z = 108.883;
const XYZ_EPSILON = 0.008856;
const XYZ_KAPPA = 903.3;

/** L*, a*, b*. */
export type Lab = readonly [number, number, number];

function constrain(amount: number, low: number, high: number): number {
  return amount < low ? low : amount > high ? high : amount;
}

function pivot(component: number): number {
  return component > XYZ_EPSILON ? Math.cbrt(component) : (XYZ_KAPPA * component + 16.0) / 116.0;
}

function companding(channel: number): number {
  const s = channel / 255.0;
  return s < 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** sRGB (0..255 per channel) to CIE XYZ (0..100). */
function rgbToXyz(color: RGB): [number, number, number] {
  const sr = companding(color[0]);
  const sg = companding(color[1]);
  const sb = companding(color[2]);
  return [
    100.0 * (sr * 0.412453 + sg * 0.35758 + sb * 0.180423),
    100.0 * (sr * 0.212671 + sg * 0.71516 + sb * 0.072169),
    100.0 * (sr * 0.019334 + sg * 0.119193 + sb * 0.950227),
  ];
}

export function rgbToLab(color: RGB): Lab {
  const xyz = rgbToXyz(color);
  const x = pivot(xyz[0] / XYZ_WHITE_REFERENCE_X);
  const y = pivot(xyz[1] / XYZ_WHITE_REFERENCE_Y);
  const z = pivot(xyz[2] / XYZ_WHITE_REFERENCE_Z);
  return [Math.max(0.0, 116.0 * y - 16.0), 500.0 * (x - y), 200.0 * (y - z)];
}

export function labToRgb(lab: Lab): RGB {
  const fy = (lab[0] + 16.0) / 116.0;
  const fx = lab[1] / 500.0 + fy;
  const fz = fy - lab[2] / 200.0;

  const fx3 = fx * fx * fx;
  const xr = fx3 > XYZ_EPSILON ? fx3 : (116.0 * fx - 16.0) / XYZ_KAPPA;
  // VOSviewer's own threshold, kept verbatim: kappa * epsilon = 7.9996248.
  const yr = lab[0] > 7.9996247999999985 ? fy * fy * fy : lab[0] / XYZ_KAPPA;
  const fz3 = fz * fz * fz;
  const zr = fz3 > XYZ_EPSILON ? fz3 : (116.0 * fz - 16.0) / XYZ_KAPPA;

  const X = xr * XYZ_WHITE_REFERENCE_X;
  const Y = yr * XYZ_WHITE_REFERENCE_Y;
  const Z = zr * XYZ_WHITE_REFERENCE_Z;

  let r = (X * 3.2406 + Y * -1.5372 + Z * -0.4986) / 100.0;
  let g = (X * -0.9689 + Y * 1.8758 + Z * 0.0415) / 100.0;
  let b = (X * 0.0557 + Y * -0.204 + Z * 1.057) / 100.0;

  const gamma = (c: number): number =>
    c > 0.0031308 ? 1.055 * Math.pow(c, 1.0 / 2.4) - 0.055 : 12.92 * c;
  r = gamma(r);
  g = gamma(g);
  b = gamma(b);

  return [
    constrain(Math.floor(r * 255.0 + 0.5), 0, 255),
    constrain(Math.floor(g * 255.0 + 0.5), 0, 255),
    constrain(Math.floor(b * 255.0 + 0.5), 0, 255),
  ];
}

/** Interpolate two colours through Lab. `ratio` 0 gives `a`, 1 gives `b`. */
export function interpolateLab(a: RGB, b: RGB, ratio: number): RGB {
  const la = rgbToLab(a);
  const lb = rgbToLab(b);
  const inv = 1.0 - ratio;
  return labToRgb([
    la[0] * inv + lb[0] * ratio,
    la[1] * inv + lb[1] * ratio,
    la[2] * inv + lb[2] * ratio,
  ]);
}

/** Plain RGB interpolation. VOSviewer uses this only for its dimmed variants. */
export function interpolateRgb(a: RGB, b: RGB, ratio: number): RGB {
  const inv = 1.0 - ratio;
  return [
    constrain(Math.floor(a[0] * inv + b[0] * ratio + 0.5), 0, 255),
    constrain(Math.floor(a[1] * inv + b[1] * ratio + 0.5), 0, 255),
    constrain(Math.floor(a[2] * inv + b[2] * ratio + 0.5), 0, 255),
  ];
}

/**
 * Move a colour towards L* = 100 by `strength`, keeping its a* and b*.
 * This is how VOSviewer makes the light half of a circle's gradient.
 */
export function lightColor(color: RGB, strength: number): RGB {
  const lab = rgbToLab(color);
  return labToRgb([(1.0 - strength) * lab[0] + strength * 100.0, lab[1], lab[2]]);
}

/** Move a colour towards L* = 0 by `strength`, keeping its a* and b*. */
export function darkColor(color: RGB, strength: number): RGB {
  const lab = rgbToLab(color);
  return labToRgb([(1.0 - strength) * lab[0], lab[1], lab[2]]);
}

/** Perceived lightness, 0..100. Used to choose readable text over a fill. */
export function lightness(color: RGB): number {
  return rgbToLab(color)[0];
}

export function css(color: RGB, alpha = 1): string {
  return alpha >= 1
    ? `rgb(${color[0]},${color[1]},${color[2]})`
    : `rgba(${color[0]},${color[1]},${color[2]},${alpha})`;
}
